import * as THREE from "three";
import type { XRRig } from "./session";
import { WORKSHOP_SIZE } from "../world/workshopLayout";

/** Walking speed in meters per second (left thumbstick). */
export const XR_MOVE_SPEED_MPS = 1.75;
/** Radial deadzone so stick noise does not drift the rig. */
export const XR_MOVE_DEADZONE = 0.18;
/** Snap-turn angle (~30°). */
export const XR_SNAP_TURN_RAD = THREE.MathUtils.degToRad(30);
/** |thumbstick X| must exceed this to fire a snap. */
export const XR_SNAP_THRESHOLD = 0.7;
/** |thumbstick X| must fall below this before another snap is allowed. */
export const XR_SNAP_RECENTER = 0.25;
/** Inset from workshop walls when clamping player xz (meters). */
export const XR_BOUNDS_INSET_M = 0.4;

const HALF_X = WORKSHOP_SIZE.width / 2 - XR_BOUNDS_INSET_M;
const HALF_Z = WORKSHOP_SIZE.depth / 2 - XR_BOUNDS_INSET_M;

/**
 * Quest controller locomotion: left stick walks, right stick snap-turns.
 *
 * Moves/rotates {@link XRRig.player} only — never the tracked camera — so
 * room-scale head pose still composes on top of the rig. Reads gamepad axes
 * only; does not consume select/squeeze. Hands-only sources (no stick) are
 * ignored this milestone.
 */
export class XRLocomotion {
  private readonly rig: XRRig;

  private readonly _forward = new THREE.Vector3();
  private readonly _right = new THREE.Vector3();
  private readonly _move = new THREE.Vector3();
  private readonly _stick = new THREE.Vector2();
  /** Headset world position scratch — snap must pivot around this, not the rig origin. */
  private readonly _head = new THREE.Vector3();

  private moveX = 0;
  private moveY = 0;
  /** True after a snap until the right stick recenters. */
  private snapLatched = false;

  constructor(rig: XRRig) {
    this.rig = rig;
  }

  /**
   * Clear cached axes and snap latch. Call on XR session start/end so a
   * deflected stick from a previous session cannot walk or snap on re-entry.
   */
  reset(): void {
    this.moveX = 0;
    this.moveY = 0;
    this._stick.set(0, 0);
    this._move.set(0, 0, 0);
    // Latch until recenter so a held stick at session start does not snap.
    this.snapLatched = true;
  }

  update(dt: number): void {
    if (!this.rig.renderer.xr.isPresenting) return;

    const session = this.rig.renderer.xr.getSession();
    if (!session) return;

    this.moveX = 0;
    this.moveY = 0;
    let rightX = 0;
    let sawRightStick = false;

    for (const source of session.inputSources) {
      // Do not guess: skip unhanded sources. Hands-only (pinch, no stick) must
      // not clobber a controller stick if both appear in inputSources.
      if (source.hand) continue;
      if (source.handedness !== "left" && source.handedness !== "right") {
        continue;
      }
      if (!readThumbstick(source.gamepad, this._stick)) continue;

      if (source.handedness === "left") {
        applyRadialDeadzone(
          this._stick.x,
          this._stick.y,
          XR_MOVE_DEADZONE,
          this._stick,
        );
        this.moveX = this._stick.x;
        this.moveY = this._stick.y;
      } else {
        rightX = this._stick.x;
        sawRightStick = true;
      }
    }

    this.applySmoothMove(dt);
    if (sawRightStick) this.applySnapTurn(rightX);
    this.clampToWorkshop();
  }

  private applySmoothMove(dt: number): void {
    if (this.moveX === 0 && this.moveY === 0) return;

    const { camera, player } = this.rig;
    // Headset facing, horizontal only — ignore pitch so look-down does not
    // walk into the floor. World direction already includes player yaw.
    camera.getWorldDirection(this._forward);
    this._forward.y = 0;
    if (this._forward.lengthSq() < 1e-8) {
      this._forward.set(0, 0, -1).applyQuaternion(player.quaternion);
      this._forward.y = 0;
    }
    this._forward.normalize();
    this._right.set(-this._forward.z, 0, this._forward.x);

    // Gamepad Y is positive toward the user; negate so forward stick walks
    // along headset look. Analog magnitude is preserved (corners clamped).
    this._move.set(0, 0, 0);
    this._move.addScaledVector(this._forward, -this.moveY);
    this._move.addScaledVector(this._right, this.moveX);
    const len = this._move.length();
    if (len > 1) this._move.multiplyScalar(1 / len);
    player.position.addScaledVector(this._move, XR_MOVE_SPEED_MPS * dt);
  }

  private applySnapTurn(stickX: number): void {
    if (this.snapLatched) {
      if (Math.abs(stickX) < XR_SNAP_RECENTER) this.snapLatched = false;
      return;
    }
    if (Math.abs(stickX) < XR_SNAP_THRESHOLD) return;

    const { camera, player } = this.rig;
    const angle = -Math.sign(stickX) * XR_SNAP_TURN_RAD;

    // Pivot around headset XZ. Rotating the rig about its origin would orbit
    // the tracked head whenever the user has a room-scale offset from spawn.
    camera.getWorldPosition(this._head);
    const hx = this._head.x;
    const hz = this._head.z;
    player.rotation.y += angle;
    player.updateMatrixWorld(true);
    camera.getWorldPosition(this._head);
    player.position.x += hx - this._head.x;
    player.position.z += hz - this._head.z;

    this.snapLatched = true;
  }

  private clampToWorkshop(): void {
    const { position } = this.rig.player;
    position.x = Math.max(-HALF_X, Math.min(HALF_X, position.x));
    position.z = Math.max(-HALF_Z, Math.min(HALF_Z, position.z));
  }
}

/**
 * Thumbstick X/Y from an XR gamepad.
 * `xr-standard`: thumbstick is axes[2], axes[3] (0,1 = unused touchpad).
 * Some Quest builds fill only 0,1 (or duplicate the stick there) while still
 * reporting 4 axes — pick the pair with larger magnitude so walk/snap is not
 * stuck reading zeros. Returns false when there is no usable stick.
 */
function readThumbstick(
  gamepad: Gamepad | undefined,
  out: THREE.Vector2,
): boolean {
  if (!gamepad) return false;
  const axes = gamepad.axes;
  if (axes.length >= 4) {
    const x0 = axes[0] ?? 0;
    const y0 = axes[1] ?? 0;
    const x1 = axes[2] ?? 0;
    const y1 = axes[3] ?? 0;
    if (x1 * x1 + y1 * y1 >= x0 * x0 + y0 * y0) {
      out.set(x1, y1);
    } else {
      out.set(x0, y0);
    }
    return true;
  }
  if (axes.length >= 2) {
    out.set(axes[0] ?? 0, axes[1] ?? 0);
    return true;
  }
  return false;
}

function applyRadialDeadzone(
  x: number,
  y: number,
  deadzone: number,
  out: THREE.Vector2,
): void {
  const mag = Math.hypot(x, y);
  if (mag < deadzone || mag === 0) {
    out.set(0, 0);
    return;
  }
  const scaled = Math.min(1, (mag - deadzone) / (1 - deadzone));
  out.set((x / mag) * scaled, (y / mag) * scaled);
}
